(() => {
  'use strict';

  if (window.__wiReelShopBooted) return;
  window.__wiReelShopBooted = true;

  const ROOT_SELECTOR = '[data-wirs-root]';
  const INIT_FLAG = 'wirsInitialized';
  const VARIANT_SECTION_ID = 'wi-reel-shop-variant-panel';

  const routeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const cloneTemplate = (template) => {
    if (!template?.content) return null;
    return template.content.cloneNode(true);
  };

  const initRoot = (root) => {
    if (!root || root.dataset[INIT_FLAG] === 'true') return;
    root.dataset[INIT_FLAG] = 'true';

    const rail = root.querySelector('[data-wirs-rail]');
    const lightbox = root.querySelector('[data-wirs-lightbox]');
    const viewer = root.querySelector('[data-wirs-viewer]');
    const lightboxVideo = root.querySelector('[data-wirs-lightbox-video]');
    const productSlot = root.querySelector('[data-wirs-product-slot]');
    const playButton = root.querySelector('[data-wirs-play]');
    const muteButton = root.querySelector('[data-wirs-mute]');
    const closeButton = root.querySelector('[data-wirs-close]');
    const prevButton = root.querySelector('[data-wirs-prev]');
    const nextButton = root.querySelector('[data-wirs-next]');
    const peekPrev = root.querySelector('[data-wirs-peek="prev"]');
    const peekNext = root.querySelector('[data-wirs-peek="next"]');
    const peekPrevImage = root.querySelector('[data-wirs-peek-image="prev"]');
    const peekNextImage = root.querySelector('[data-wirs-peek-image="next"]');
    const variantSheet = root.querySelector('[data-wirs-variant-sheet]');
    const variantSlot = root.querySelector('[data-wirs-variant-slot]');
    const variantBackdrop = root.querySelector('[data-wirs-variant-backdrop]');
    const toast = root.querySelector('[data-wirs-toast]');
    const toastName = root.querySelector('[data-wirs-toast-name]');

    if (!lightbox || !viewer || !lightboxVideo || !productSlot || !playButton || !muteButton || !closeButton || !variantSheet || !variantSlot || !variantBackdrop) {
      root.dataset[INIT_FLAG] = 'false';
      return;
    }

    const cards = Array.from(root.querySelectorAll('[data-wirs-card]'));
    const stageQuery = window.matchMedia?.('(min-width: 900px)') || null;
    const baseRoute = routeRoot();
    const cartAddUrl = `${baseRoute}cart/add.js`;
    const labels = {
      turnSoundOn: root.dataset.labelTurnSoundOn || 'Turn sound on',
      muteVideo: root.dataset.labelMuteVideo || 'Mute video',
      playVideo: root.dataset.labelPlayVideo || 'Play video',
      pauseVideo: root.dataset.labelPauseVideo || 'Pause video',
      previousReel: root.dataset.labelPreviousReel || 'Previous reel',
      nextReel: root.dataset.labelNextReel || 'Next reel',
      reel: root.dataset.labelReel || 'Product video',
      addToCart: root.dataset.labelAddToCart || 'Add to cart',
      addToCartWithPrice: root.dataset.labelAddToCartWithPrice || 'Add to cart · __PRICE__',
      soldOut: root.dataset.labelSoldOut || 'Sold out',
      checkingAvailability: root.dataset.labelCheckingAvailability || 'Checking availability...',
      availabilityError: root.dataset.labelAvailabilityError || 'Unavailable combination',
      adding: root.dataset.labelAdding || 'Adding...',
      added: root.dataset.labelAdded || 'Added!',
      tryAgain: root.dataset.labelTryAgain || 'Try again'
    };
    const withPrice = (price = '') =>
      labels.addToCartWithPrice.replace('__PRICE__', price).trim();

    let currentIndex = 0;
    let lastFocus = null;
    let previousHtmlOverflow = '';
    let previousBodyOverflow = '';
    let variantController = null;
    let toastTimer = 0;
    let isAdding = false;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let destroyed = false;
    let userUnmutedAudio = false;
    const variantCache = new Map();

    const autoplayMode = root.dataset.wirsAutoplay || 'all';
    const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
    const previewRatios = new Map();
    const previewPlaying = new Set();
    const previewTimers = new Map();

    const PREVIEW_VISIBLE_RATIO = 0.2;
    const PREVIEW_STAGGER_MS = 70;
    let previewObserver = null;
    let previewSyncQueued = false;
    let previewStarted = false;
    let previewFailures = 0;
    let previewDisabled = false;

    const previewConnection = () =>
      navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

    const previewsAllowed = () => {
      if (previewDisabled || autoplayMode === 'off') return false;
      if (reducedMotionQuery?.matches) return false;

      const connection = previewConnection();
      if (connection?.saveData) return false;
      if (['slow-2g', '2g', '3g'].includes(connection?.effectiveType || '')) return false;
      if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory > 0 && navigator.deviceMemory < 2) return false;
      if (autoplayMode === 'desktop' && !stageQuery?.matches) return false;

      return true;
    };

    const attachPreview = (card) => {
      if (card.__wirsPreview) return card.__wirsPreview;

      const src = card.dataset.preview || card.dataset.video || '';
      const media = card.querySelector('.wi-reel-shop__media');
      if (!src || !media) return null;

      const video = document.createElement('video');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      video.dataset.wirsPreview = '';
      video.dataset.src = src;
      video.width = 405;
      video.height = 720;

      media.appendChild(video);
      card.__wirsPreview = video;
      return video;
    };

    const startPreview = (card) => {
      previewTimers.delete(card);
      if (destroyed || !previewPlaying.has(card)) return;

      const video = attachPreview(card);
      if (!video) {
        previewPlaying.delete(card);
        return;
      }

      if (!video.getAttribute('src')) {
        video.setAttribute('src', video.dataset.src);
        video.load();
      }

      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          previewPlaying.delete(card);
          video.classList.remove('is-playing');
          previewFailures += 1;
          if (previewFailures >= 3) {
            previewDisabled = true;
            teardownPreviews();
          }
        });
      }
    };

    const queuePreview = (card, order) => {
      if (previewPlaying.has(card)) return;
      previewPlaying.add(card);

      if (order <= 0) {
        startPreview(card);
        return;
      }

      previewTimers.set(card, window.setTimeout(() => startPreview(card), order * PREVIEW_STAGGER_MS));
    };

    const stopPreview = (card, release = false) => {
      previewPlaying.delete(card);

      const timer = previewTimers.get(card);
      if (timer) {
        window.clearTimeout(timer);
        previewTimers.delete(card);
      }

      const video = card.__wirsPreview;
      if (!video) return;

      video.classList.remove('is-playing');
      if (!video.paused) video.pause();

      if (release && video.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    };

    const pauseAllPreviews = (release = false) => {
      cards.forEach((card) => stopPreview(card, release));
    };

    const syncPreviews = () => {
      previewSyncQueued = false;
      if (destroyed) return;

      if (!previewsAllowed() || document.hidden || lightbox.classList.contains('is-open')) {
        pauseAllPreviews();
        return;
      }

      let order = 0;
      cards.forEach((card) => {
        const ratio = previewRatios.get(card) || 0;
        if (ratio >= PREVIEW_VISIBLE_RATIO) {
          if (!previewPlaying.has(card)) {
            queuePreview(card, order);
            order += 1;
          }
        } else {
          stopPreview(card, false);
        }
      });
    };

    const queuePreviewSync = () => {
      if (previewSyncQueued || destroyed) return;
      previewSyncQueued = true;
      window.requestAnimationFrame(syncPreviews);
    };

    const startPreviews = () => {
      if (previewStarted || destroyed || !previewsAllowed() || !cards.length) return;
      if (typeof window.IntersectionObserver !== 'function') return;

      previewStarted = true;
      previewObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          previewRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        queuePreviewSync();
      }, { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] });

      cards.forEach((card) => previewObserver.observe(card));
    };

    const teardownPreviews = () => {
      previewObserver?.disconnect();
      previewObserver = null;
      previewStarted = false;
      previewTimers.forEach((timer) => window.clearTimeout(timer));
      previewTimers.clear();
      pauseAllPreviews(true);
      previewRatios.clear();
    };

    const deferPreviewStart = () => {
      if (autoplayMode === 'off') return;

      const schedule = () => {
        const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 250));
        idle(startPreviews, { timeout: 2500 });
      };

      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true });
    };

    const handlePreviewVisibility = () => queuePreviewSync();
    const getCard = (index) => cards[index] || null;
    const getTemplate = (type, index) => root.querySelector(`[data-wirs-${type}-template="${index}"]`);

    const setMuteIcon = () => {
      const isMuted = lightboxVideo.muted;
      muteButton.classList.toggle('is-muted', isMuted);
      muteButton.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
      muteButton.setAttribute('aria-label', isMuted ? labels.turnSoundOn : labels.muteVideo);
    };

    const setPlaybackIcon = () => {
      const playing = !lightboxVideo.paused && !lightboxVideo.ended;
      playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
      playButton.setAttribute('aria-label', playing ? labels.pauseVideo : labels.playVideo);
    };

    const hideToast = () => {
      toast?.classList.remove('is-on');
      if (toastTimer) {
        window.clearTimeout(toastTimer);
        toastTimer = 0;
      }
    };

    const showToast = (name) => {
      if (!toast) return;
      if (toastName) toastName.textContent = name || '';
      toast.classList.add('is-on');
      if (toastTimer) window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(hideToast, 3600);
    };

    const warmedProductImages = new Map();
    const warmedPeekPosters = new Map();
    const getQuickCard = () => productSlot.querySelector('[data-wirs-quick-card]');

    const warmProductImage = (card) => {
      if (!card) return;
      const src = card.dataset.productImage || '';
      if (!src || warmedProductImages.has(src)) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
      warmedProductImages.set(src, image);
    };

    const warmedVideos = new Map();
    const warmReelVideo = (card) => {
      if (!card) return;
      const src = card.dataset.video || '';
      if (!src || warmedVideos.has(src)) return;

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection?.saveData) return;
      if (['slow-2g', '2g'].includes(connection?.effectiveType || '')) return;

      const warm = document.createElement('video');
      warm.muted = true;
      warm.preload = 'metadata';
      warm.src = src;
      warmedVideos.set(src, warm);
    };

    const canWarmPeekPosters = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!stageQuery?.matches || connection?.saveData) return false;
      return !['slow-2g', '2g'].includes(connection?.effectiveType || '');
    };

    const getWrappedIndex = (index) => {
      if (!cards.length) return 0;
      return (index + cards.length) % cards.length;
    };

    const warmPeekPoster = (index) => {
      if (!canWarmPeekPosters()) return;
      const card = getCard(getWrappedIndex(index));
      const src = card?.dataset.peekPoster || card?.dataset.poster || '';
      if (!src || warmedPeekPosters.has(src)) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
      warmedPeekPosters.set(src, image);
    };

    const warmAdjacentPosters = (index) => {
      if (cards.length < 2) return;
      warmPeekPoster(index - 1);
      warmPeekPoster(index + 1);
    };

    const setPeekPreview = (button, image, index, label) => {
      if (!button || !image) return;
      const card = getCard(getWrappedIndex(index));
      const src = card?.dataset.peekPoster || card?.dataset.poster || '';

      if (!card || !src) {
        button.hidden = true;
        image.removeAttribute('src');
        return;
      }

      button.dataset.index = String(getWrappedIndex(index));
      button.setAttribute('aria-label', `${label}: ${card.getAttribute('aria-label') || labels.reel}`);
      button.hidden = false;

      if (image.dataset.currentSrc !== src) {
        image.classList.add('is-changing');
        image.src = src;
        image.dataset.currentSrc = src;
        image.addEventListener('load', () => {
          window.requestAnimationFrame(() => image.classList.remove('is-changing'));
        }, { once: true });
      }
    };

    const syncPeekPreviews = (index = currentIndex) => {
      if (!peekPrev || !peekNext || !peekPrevImage || !peekNextImage) return;

      if (!stageQuery?.matches || cards.length < 2) {
        peekPrev.hidden = true;
        peekNext.hidden = true;
        return;
      }

      if (cards.length === 2) {
        peekPrev.hidden = true;
        setPeekPreview(peekNext, peekNextImage, index + 1, labels.nextReel);
        return;
      }

      setPeekPreview(peekPrev, peekPrevImage, index - 1, labels.previousReel);
      setPeekPreview(peekNext, peekNextImage, index + 1, labels.nextReel);
      warmAdjacentPosters(index);
    };

    const renderProductCard = (index) => {
      productSlot.textContent = '';
      const template = getTemplate('product', index);
      const fragment = cloneTemplate(template);
      if (!fragment) return;

      const card = getCard(index);
      const image = fragment.querySelector('[data-wirs-card-image]');

      if (image) {
        const src = image.dataset.src || card?.dataset.productImage || '';
        const fallback = card?.dataset.poster || '';

        if (src) {
          image.fetchPriority = 'high';
          image.src = src;
          image.addEventListener('error', () => {
            if (fallback && image.src !== fallback) image.src = fallback;
          }, { once: true });
        } else if (fallback) {
          image.src = fallback;
        }
      }

      productSlot.appendChild(fragment);
    };

    const renderVariantSeed = (index) => {
      variantSlot.textContent = '';
      const template = getTemplate('variant', index);
      const fragment = cloneTemplate(template);
      if (fragment) variantSlot.appendChild(fragment);
    };

    let stallTimer = 0;
    let stallRetried = false;

    const setBuffering = (on) => {
      viewer.classList.toggle('is-buffering', Boolean(on));
    };

    const clearStallWatch = () => {
      if (stallTimer) {
        window.clearTimeout(stallTimer);
        stallTimer = 0;
      }
    };

    const watchForStall = () => {
      clearStallWatch();
      stallTimer = window.setTimeout(() => {
        stallTimer = 0;
        if (!lightbox.classList.contains('is-open')) return;
        if (lightboxVideo.readyState >= 3) return;

        if (lightboxVideo.buffered.length > 0 && lightboxVideo.buffered.end(0) > 0.5) {
          setBuffering(true);
          return;
        }

        setBuffering(true);
        if (stallRetried) return;
        stallRetried = true;
        lightboxVideo.load();
        lightboxVideo.play().catch(() => {});
      }, 1600);
    };

    const loadLightboxMedia = (index) => {
      const card = getCard(index);
      if (!card) return;

      const poster = card.dataset.poster || '';
      const video = card.dataset.video || '';

      clearStallWatch();
      stallRetried = false;

      lightboxVideo.pause();
      lightboxVideo.removeAttribute('src');
      lightboxVideo.load();
      setPlaybackIcon();

      if (poster) lightboxVideo.poster = poster;
      else lightboxVideo.removeAttribute('poster');

      if (!video) {
        setBuffering(false);
        setPlaybackIcon();
        return;
      }

      setBuffering(false);
      lightboxVideo.preload = 'auto';

      lightboxVideo.muted = !userUnmutedAudio;
      setMuteIcon();

      lightboxVideo.setAttribute('src', video);
      lightboxVideo.load();

      const attempt = lightboxVideo.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt
          .catch(() => {
            if (!lightboxVideo.muted) {
              lightboxVideo.muted = true;
              userUnmutedAudio = false;
              setMuteIcon();
              lightboxVideo.play().catch(() => {});
            }
          })
          .finally(() => {
            setPlaybackIcon();
            watchForStall();
          });
      } else {
        setPlaybackIcon();
        watchForStall();
      }
    };

    const closeVariantSheet = () => {
      if (variantController) {
        variantController.abort();
        variantController = null;
      }
      variantSheet.classList.remove('is-open');
      variantSheet.setAttribute('aria-hidden', 'true');
      variantBackdrop.hidden = true;
    };

    const openVariantSheet = () => {
      renderVariantSeed(currentIndex);
      const selector = variantSlot.querySelector('[data-wirs-variant-selector]');
      if (!selector) return;

      variantBackdrop.hidden = false;
      variantSheet.classList.add('is-open');
      variantSheet.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(() => {
        selector.querySelector('[data-wirs-option][aria-pressed="true"], [data-wirs-option], [data-wirs-variant-confirm]')?.focus();
      });
    };

    const loadReel = (requestedIndex) => {
      if (!cards.length) return;
      let index = requestedIndex;
      if (index < 0) index = cards.length - 1;
      if (index >= cards.length) index = 0;
      currentIndex = index;

      closeVariantSheet();
      renderProductCard(index);
      syncPeekPreviews(index);
      loadLightboxMedia(index);
    };

    const openLightbox = (index) => {
      lastFocus = document.activeElement;
      previousHtmlOverflow = document.documentElement.style.overflow;
      previousBodyOverflow = document.body.style.overflow;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      warmProductImage(getCard(index));
      warmAdjacentPosters(index);
      pauseAllPreviews(true);

      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');

      loadReel(index);
      window.requestAnimationFrame(() => closeButton.focus());
    };

    const closeLightbox = () => {
      if (!lightbox.classList.contains('is-open')) return;

      closeVariantSheet();
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');

      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;

      clearStallWatch();
      setBuffering(false);
      lightboxVideo.pause();
      setPlaybackIcon();
      lightboxVideo.removeAttribute('src');
      lightboxVideo.load();

      productSlot.textContent = '';
      variantSlot.textContent = '';
      if (peekPrev) peekPrev.hidden = true;
      if (peekNext) peekNext.hidden = true;

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }

      queuePreviewSync();
    };

    const setQuickCardImage = (src) => {
      if (!src) return;
      const image = getQuickCard()?.querySelector('[data-wirs-card-image]');
      if (!image) return;
      if (image.src !== src) image.src = src;
    };

    const updateQuickCardFromSelector = (selector) => {
      const card = getQuickCard();
      if (!card || !selector) return;

      const title = selector.dataset.productTitle || '';
      const productLink = selector.dataset.productLink || selector.dataset.productUrl || '#';
      const image = selector.dataset.productImage || '';
      const price = selector.dataset.price || '';
      const compare = selector.dataset.compare || '';
      const discount = selector.dataset.discount || '';
      const unitPrice = selector.dataset.unitPrice || '';
      const variantId = selector.dataset.variantId || '';
      const available = selector.dataset.variantAvailable === 'true';

      card.dataset.productUrl = productLink;

      card.querySelectorAll('[data-wirs-card-title]').forEach((element) => {
        element.textContent = title;
        element.href = productLink;
      });

      card.querySelectorAll('[data-wirs-card-link], [data-wirs-card-view]').forEach((element) => {
        element.href = productLink;
      });

      if (image) setQuickCardImage(image);

      const priceElement = card.querySelector('[data-wirs-card-price]');
      if (priceElement) priceElement.textContent = price;

      const compareElement = card.querySelector('[data-wirs-card-compare]');
      if (compareElement) {
        compareElement.textContent = compare;
        compareElement.hidden = !compare;
      }

      const discountElement = card.querySelector('[data-wirs-card-discount]');
      if (discountElement) {
        discountElement.textContent = discount;
        discountElement.hidden = !discount;
      }

      const unitPriceElement = card.querySelector('[data-wirs-card-unit-price]');
      if (unitPriceElement) {
        unitPriceElement.textContent = unitPrice;
        unitPriceElement.hidden = !unitPrice;
      }

      const addButton = card.querySelector('[data-wirs-product-add]');
      if (addButton) {
        addButton.dataset.variantId = variantId;
        addButton.disabled = !available;
        addButton.textContent = available ? labels.addToCart : labels.soldOut;
      }
    };

    const getOptionGroups = (selector) =>
      Array.from(selector.querySelectorAll('.wi-reel-shop__variant-option[data-option-position]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

    const markOptionSelected = (button) => {
      const group = button.closest('.wi-reel-shop__variant-option');
      if (!group) return;

      group.querySelectorAll('[data-wirs-option]').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
      });
    };

    const selectedOptionIds = (selector) =>
      getOptionGroups(selector)
        .map((group) => {
          const selected = group.querySelector('[data-wirs-option][aria-pressed="true"]');
          return selected?.dataset.optionValueId || '';
        })
        .filter(Boolean);

    const setSelectorStatus = (selector, message = '', type = '') => {
      const status = selector?.querySelector('[data-wirs-variant-status]');
      if (!status) return;

      status.textContent = message;
      status.hidden = !message;
      if (type) status.dataset.state = type;
      else status.removeAttribute('data-state');
    };

    const applyVariantData = (selector, variant) => {
      if (!selector || !variant) return;

      const available = Boolean(variant.available);
      const variantId = String(variant.id || '');
      const image = variant.image || '';

      selector.dataset.variantId = variantId;
      selector.dataset.variantAvailable = String(available);
      selector.dataset.price = variant.price || '';
      selector.dataset.compare = variant.compare || '';
      selector.dataset.discount = variant.discount || '';
      selector.dataset.unitPrice = variant.unitPrice || '';

      if (image) selector.dataset.productImage = image;

      const confirm = selector.querySelector('[data-wirs-variant-confirm]');
      if (confirm) {
        confirm.dataset.variantId = variantId;
        confirm.disabled = !available;
        confirm.textContent = available
          ? withPrice(variant.price || '')
          : labels.soldOut;
      }

      setSelectorStatus(selector);
      updateQuickCardFromSelector(selector);
    };

    const applyDirectVariantFromButton = (selector, button) => {
      const id = button.dataset.directVariantId || '';
      if (!id) return false;

      applyVariantData(selector, {
        id,
        available: button.dataset.directAvailable === 'true',
        price: button.dataset.directPrice || '',
        compare: button.dataset.directCompare || '',
        discount: button.dataset.directDiscount || '',
        unitPrice: button.dataset.directUnitPrice || '',
        image: button.dataset.directImage || ''
      });

      return true;
    };

    const fetchVariantSelector = async (selector, preferredProductUrl = '', activeValueId = '') => {
      if (!selector) return;

      const ids = selectedOptionIds(selector);
      if (!ids.length) return;

      const selectedButton = selector.querySelector('[data-wirs-option][aria-pressed="true"][data-product-url]');
      const requestedProductUrl = preferredProductUrl || selectedButton?.dataset.productUrl || selector.dataset.productUrl;
      if (!requestedProductUrl) return;

      const url = new URL(requestedProductUrl, window.location.origin);
      url.searchParams.set('section_id', VARIANT_SECTION_ID);
      url.searchParams.set('option_values', ids.join(','));

      const cacheKey = url.toString();
      const controller = new AbortController();

      if (variantController) variantController.abort();
      variantController = controller;

      const slowStatusTimer = window.setTimeout(() => {
        if (selector.isConnected) {
          setSelectorStatus(selector, labels.checkingAvailability, 'loading');
        }
      }, 450);

      try {
        let html = variantCache.get(cacheKey);

        if (!html) {
          const response = await fetch(cacheKey, {
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });

          if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
          html = await response.text();
          variantCache.set(cacheKey, html);

          if (variantCache.size > 18) {
            variantCache.delete(variantCache.keys().next().value);
          }
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextSelector = doc.querySelector('[data-wirs-variant-selector]');

        if (!nextSelector) throw new Error('Selector missing from response.');

        const imported = document.importNode(nextSelector, true);
        selector.replaceWith(imported);
        updateQuickCardFromSelector(imported);

        if (activeValueId) {
          const matchingChip = imported.querySelector(`[data-option-value-id="${activeValueId}"]`);
          matchingChip?.focus();
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (selector.isConnected) {
          setSelectorStatus(selector, labels.availabilityError, 'error');
        }
      } finally {
        window.clearTimeout(slowStatusTimer);
        if (variantController === controller) variantController = null;
      }
    };

    const handleVariantOption = (button) => {
      const selector = button.closest('[data-wirs-variant-selector]');
      if (!selector) return;

      if (button.getAttribute('aria-pressed') === 'true') return;

      const currentProductUrl = selector.dataset.productUrl || '';
      const nextProductUrl = button.dataset.productUrl || currentProductUrl;
      const switchesProduct = Boolean(nextProductUrl && currentProductUrl && nextProductUrl !== currentProductUrl);
      const activeValueId = button.dataset.optionValueId || '';

      markOptionSelected(button);
      const optionCount = Number.parseInt(selector.dataset.optionCount || '0', 10);

      if (!switchesProduct && optionCount === 1 && applyDirectVariantFromButton(selector, button)) {
        return;
      }

      const confirmButton = selector.querySelector('[data-wirs-variant-confirm]');
      if (confirmButton && optionCount > 1) {
        confirmButton.disabled = true;
        confirmButton.textContent = labels.checkingAvailability;
      }

      fetchVariantSelector(selector, nextProductUrl, activeValueId);
    };

    const setAddButtonState = (button, state, label = '') => {
      if (!button) return;

      button.classList.toggle('is-loading', state === 'loading');
      button.classList.toggle('is-added', state === 'added');

      if (state === 'loading') {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }

      if (label) button.textContent = label;
    };

    const addToCart = async (variantId, button) => {
      if (isAdding || !variantId) return;
      isAdding = true;

      const original = button?.textContent || labels.addToCart;
      const quickCard = getQuickCard();
      const productName = quickCard?.querySelector('[data-wirs-card-title]')?.textContent?.trim() || '';

      if (variantController) {
        variantController.abort();
        variantController = null;
      }

      setAddButtonState(button, 'loading', labels.adding);

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: 'cart-drawer'
          })
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw result;

        setAddButtonState(button, 'added', labels.added);
        closeVariantSheet();
        showToast(productName);
        isAdding = false;

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true, detail: { cart: result } }));
        document.dispatchEvent(
          new CustomEvent('wi:cart-item-added', {
            bubbles: true,
            detail: { item: result, sections: result.sections || null, source: 'wi-reel-shop' }
          })
        );

        const dawnDrawer = document.querySelector('cart-drawer');
        if (dawnDrawer && result.sections) {
          dawnDrawer.renderContents?.({ sections: result.sections });
        }

        if (button) {
          window.setTimeout(() => {
            if (!button.isConnected) return;
            button.classList.remove('is-added');
            setAddButtonState(button, 'idle', original);
          }, 1300);
        }
      } catch (error) {
        isAdding = false;
        const errorMessage = error?.description || error?.message || labels.tryAgain;

        if (button) {
          button.classList.remove('is-added');
          setAddButtonState(button, 'idle', errorMessage);
          window.setTimeout(() => {
            if (button.isConnected) setAddButtonState(button, 'idle', original);
          }, 1800);
        }
      }
    };

    const handleRootClick = (event) => {
      const card = event.target.closest('[data-wirs-card]');
      if (card && root.contains(card)) {
        const index = Number.parseInt(card.dataset.index, 10) || 0;
        openLightbox(index);
        return;
      }

      if (event.target.closest('[data-wirs-close]')) {
        closeLightbox();
        return;
      }

      if (event.target.closest('[data-wirs-play]')) {
        if (lightboxVideo.paused || lightboxVideo.ended) {
          lightboxVideo.play().catch(() => {}).finally(setPlaybackIcon);
        } else {
          lightboxVideo.pause();
          setPlaybackIcon();
        }
        return;
      }

      if (event.target.closest('[data-wirs-mute]')) {
        lightboxVideo.muted = !lightboxVideo.muted;
        userUnmutedAudio = !lightboxVideo.muted;
        setMuteIcon();
        return;
      }

      const peek = event.target.closest('[data-wirs-peek]');
      if (peek && lightbox.contains(peek)) {
        const index = Number.parseInt(peek.dataset.index, 10);
        if (Number.isFinite(index)) loadReel(index);
        return;
      }

      if (event.target.closest('[data-wirs-prev]')) {
        loadReel(currentIndex - 1);
        return;
      }

      if (event.target.closest('[data-wirs-next]')) {
        loadReel(currentIndex + 1);
        return;
      }

      const addButton = event.target.closest('[data-wirs-product-add]');
      if (addButton) {
        if (addButton.dataset.requiresOptions === 'true') openVariantSheet();
        else addToCart(addButton.dataset.variantId, addButton);
        return;
      }

      const optionButton = event.target.closest('[data-wirs-option]');
      if (optionButton && variantSlot.contains(optionButton)) {
        handleVariantOption(optionButton);
        return;
      }

      const confirmButton = event.target.closest('[data-wirs-variant-confirm]');
      if (confirmButton && variantSlot.contains(confirmButton)) {
        addToCart(confirmButton.dataset.variantId, confirmButton);
        return;
      }

      if (event.target.closest('[data-wirs-variant-close]')) {
        closeVariantSheet();
      }
    };

    const handleBackdropClick = (event) => {
      if (event.target === lightbox) closeLightbox();
      if (event.target === variantBackdrop) closeVariantSheet();
    };

    const handleKeydown = (event) => {
      if (!lightbox.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (variantSheet.classList.contains('is-open')) closeVariantSheet();
        else closeLightbox();
        return;
      }

      if (!variantSheet.classList.contains('is-open')) {
        if (event.key === 'ArrowLeft' && prevButton) {
          event.preventDefault();
          loadReel(currentIndex - 1);
          return;
        }
        if (event.key === 'ArrowRight' && nextButton) {
          event.preventDefault();
          loadReel(currentIndex + 1);
          return;
        }
      }

      if (event.key !== 'Tab') return;

      const focusRoot = variantSheet.classList.contains('is-open') ? variantSheet : lightbox;
      const focusable = Array.from(focusRoot.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden');

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handlePointerDown = (event) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (variantSheet.classList.contains('is-open')) return;
      if (event.target.closest('button, a, input, select, [data-wirs-quick-card]')) return;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    };

    const handlePointerUp = (event) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (variantSheet.classList.contains('is-open')) return;
      if (event.target.closest('button, a, input, select, [data-wirs-quick-card]')) return;
      const dx = event.clientX - pointerStartX;
      const dy = event.clientY - pointerStartY;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
      loadReel(dx < 0 ? currentIndex + 1 : currentIndex - 1);
    };

    const handleWarmIntent = (event) => {
      const card = event.target.closest?.('[data-wirs-card]');
      if (card && root.contains(card)) {
        warmProductImage(card);
        warmReelVideo(card);
        const index = Number.parseInt(card.dataset.index, 10);
        if (Number.isFinite(index)) warmAdjacentPosters(index);
      }
    };

    root.addEventListener('click', handleRootClick);
    root.addEventListener('pointerover', handleWarmIntent, { passive: true });
    root.addEventListener('pointerdown', handleWarmIntent, { passive: true });
    lightbox.addEventListener('click', handleBackdropClick);
    viewer.addEventListener('pointerdown', handlePointerDown, { passive: true });
    viewer.addEventListener('pointerup', handlePointerUp, { passive: true });
    document.addEventListener('keydown', handleKeydown);

    const handleStageQueryChange = () => {
      if (lightbox.classList.contains('is-open')) syncPeekPreviews(currentIndex);
      if (previewStarted) queuePreviewSync();
      else deferPreviewStart();
    };

    if (stageQuery) {
      if (typeof stageQuery.addEventListener === 'function') stageQuery.addEventListener('change', handleStageQueryChange);
      else stageQuery.addListener?.(handleStageQueryChange);
    }

    document.addEventListener('visibilitychange', handlePreviewVisibility);
    if (reducedMotionQuery) {
      if (typeof reducedMotionQuery.addEventListener === 'function') reducedMotionQuery.addEventListener('change', handlePreviewVisibility);
      else reducedMotionQuery.addListener?.(handlePreviewVisibility);
    }

    deferPreviewStart();

    const handleDesignModeBlockSelect = (event) => {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const selectedCard = root.querySelector(`[data-block-id="${blockId}"]`);
      if (selectedCard) {
        selectedCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        const index = Number.parseInt(selectedCard.dataset.index, 10);
        if (Number.isFinite(index) && lightbox.classList.contains('is-open')) {
          loadReel(index);
        }
      }
      queuePreviewSync();
    };

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', handleDesignModeBlockSelect);
      document.addEventListener('shopify:block:deselect', queuePreviewSync);
      document.addEventListener('shopify:section:select', queuePreviewSync);
    }

    lightboxVideo.addEventListener('play', setPlaybackIcon);
    lightboxVideo.addEventListener('pause', setPlaybackIcon);
    lightboxVideo.addEventListener('ended', setPlaybackIcon);

    const handleLightboxWaiting = () => setBuffering(true);
    const handleLightboxPlaying = () => {
      clearStallWatch();
      setBuffering(false);
      setPlaybackIcon();
      if (cards.length > 1) {
        warmReelVideo(getCard(getWrappedIndex(currentIndex + 1)));
        warmReelVideo(getCard(getWrappedIndex(currentIndex - 1)));
      }
    };

    lightboxVideo.addEventListener('waiting', handleLightboxWaiting);
    lightboxVideo.addEventListener('stalled', handleLightboxWaiting);
    lightboxVideo.addEventListener('playing', handleLightboxPlaying);
    lightboxVideo.addEventListener('canplay', () => {
      clearStallWatch();
      setBuffering(false);
    });
    lightboxVideo.addEventListener('error', () => setBuffering(false));

    setPlaybackIcon();

    root.__wirsCleanup = () => {
      if (destroyed) return;
      destroyed = true;

      root.removeEventListener('click', handleRootClick);
      root.removeEventListener('pointerover', handleWarmIntent);
      root.removeEventListener('pointerdown', handleWarmIntent);
      lightbox.removeEventListener('click', handleBackdropClick);
      viewer.removeEventListener('pointerdown', handlePointerDown);
      viewer.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('visibilitychange', handlePreviewVisibility);

      if (reducedMotionQuery) {
        if (typeof reducedMotionQuery.removeEventListener === 'function') reducedMotionQuery.removeEventListener('change', handlePreviewVisibility);
        else reducedMotionQuery.removeListener?.(handlePreviewVisibility);
      }

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:block:select', handleDesignModeBlockSelect);
        document.removeEventListener('shopify:block:deselect', queuePreviewSync);
        document.removeEventListener('shopify:section:select', queuePreviewSync);
      }

      teardownPreviews();
      if (stageQuery) {
        if (typeof stageQuery.removeEventListener === 'function') stageQuery.removeEventListener('change', handleStageQueryChange);
        else stageQuery.removeListener?.(handleStageQueryChange);
      }

      variantController?.abort();
      variantController = null;
      if (toastTimer) window.clearTimeout(toastTimer);
      clearStallWatch();

      lightboxVideo.removeEventListener('waiting', handleLightboxWaiting);
      lightboxVideo.removeEventListener('stalled', handleLightboxWaiting);
      lightboxVideo.removeEventListener('playing', handleLightboxPlaying);

      warmedVideos.forEach((element) => {
        element.removeAttribute('src');
        element.load();
      });
      warmedVideos.clear();
      lightboxVideo.pause();

      if (lightbox.classList.contains('is-open')) {
        document.documentElement.style.overflow = previousHtmlOverflow;
        document.body.style.overflow = previousBodyOverflow;
      }

      delete root.__wirsCleanup;
      root.dataset[INIT_FLAG] = 'false';
    };
  };

  const cleanupRoot = (root) => {
    if (root && typeof root.__wirsCleanup === 'function') root.__wirsCleanup();
  };

  const boot = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target || document));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (!section) return;
    if (section.matches?.(ROOT_SELECTOR)) cleanupRoot(section);
    section.querySelectorAll?.(ROOT_SELECTOR).forEach(cleanupRoot);
  });
})();